import { useMemo, useCallback } from "react";
import {
  EntitySelector,
  EntitySelectorContent,
  Button,
  Icon,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  useEntitySelector,
  type EntitySelectorConfig,
  type EntitySelectorVariant,
  type EntitySelection,
} from "@salestrak/ui";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useStagesDialogActions } from "#app/lib/stages-dialog-context";
import { useStagePool } from "./hooks";
import {
  stageToPoolItem,
  type StagePoolStage,
} from "./hooks/use-stage-pool";
import { type SelectorSize } from "./sizes";
import { selectionToStageEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import type { Omittable } from "@salestrak/ui";

export type StageSelectorProps = Omittable<{
  /** Workflow to load stages from */
  workflowId: string | null | undefined;
  /** Currently selected stage ID */
  value?: string | null;
  /** Selected Stage snapshot for a stable trigger before the pool is ready. */
  selectedStage?: StagePoolStage | null;
  /** Called when a stage is selected */
  onValueChange?: (stageId: string) => void;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  emptyTextVariant?: React.ComponentProps<typeof SelectionEntityTrigger>["emptyTextVariant"];
  size?: SelectorSize;
  /** Span the column the picker sits in. */
  fill?: boolean;
  /** Wear the Select field's anatomy (hairline, fill, chevron): the trigger
   *  for a form Field, beside real Selects. */
  field?: boolean;
  disabled?: boolean;
  /** Render a static, non-dimmed value (no picker) — view/comment tier. */
  readOnly?: boolean;
  /** Open-state changes. An `inline` picker has no popover of its own, so
   *  its host listens for `false` — the "…" stage settings closing the
   *  picker before the dialog — to close the popover it painted. */
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}>;

export function StageSelector({
  workflowId,
  value,
  selectedStage,
  onValueChange,
  variant,
  placeholder = "Select stage...",
  emptyTextVariant,
  size,
  fill,
  field,
  disabled,
  readOnly,
  onOpenChange,
  children,
}: StageSelectorProps) {
  const { pool } = useStagePool(workflowId);
  const canEditStages = usePermission("manage.edit_workflows");

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [
        canEditStages && workflowId
          ? { ...pool, sectionTrailing: <StageSettingsAction workflowId={workflowId} /> }
          : pool,
      ],
      maxSelections: 1,
      searchPlaceholder: "Search stages...",
      emptyMessage: "No stages found",
      noItemsMessage: "This workflow has no stages",
    }),
    [pool, canEditStages, workflowId],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    if (!value) return [];
    const entity =
      pool.items.find((item) => item.id === value) ??
      (selectedStage?.stageId === value
        ? stageToPoolItem(selectedStage)
        : undefined);
    if (!entity) return [];
    return [{ id: value, poolId: pool.id, entity }];
  }, [value, pool, selectedStage]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      const selected = next[0];
      if (selected) onValueChange?.(selected.id);
    },
    [onValueChange],
  );

  const toEntity = useCallback(
    (sel: EntitySelection) => selectionToStageEntity(sel, workflowId ?? undefined),
    [workflowId],
  );

  return (
    <EntitySelector
      title="Stage"
      variant={variant}
      config={config}
      value={selections}
      onValueChange={handleValueChange}
      onOpenChange={onOpenChange}
    >
      {children ?? (
        <>
          <SelectionEntityTrigger
            toEntity={toEntity}
            placeholder={placeholder}
            emptyTextVariant={emptyTextVariant}
            title="Stage"
            size={size}
            fill={fill}
            field={field}
            fieldEmptyLabel={field ? placeholder : undefined}
            disabled={disabled}
            readOnly={readOnly}
          />
          <EntitySelectorContent>
            <EntitySelectorInput />
            <EntitySelectorPool value={pool.id} />
            <EntitySelectorEmpty>No stages found</EntitySelectorEmpty>
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}

// The picker's escape to the stage manager. It closes the picker first: a
// popup left open under the dialog it launched is answering a question the
// dialog took over. Same permission the board's column menu and the workflow
// menu gate their "Edit stages" on.
/** The workflow's stage settings, as the "…" on the list's first category
 *  label (whichever leads) — the popover's one door to where its rows are
 *  managed. Closes the picker and opens the Stages dialog. */
function StageSettingsAction({ workflowId }: { workflowId: string }) {
  const { onOpenChange } = useEntitySelector();
  const { openStagesDialog } = useStagesDialogActions();
  const handleClick = useCallback(() => {
    onOpenChange(false);
    openStagesDialog(workflowId);
  }, [onOpenChange, openStagesDialog, workflowId]);
  return (
    <Button variant="ghost" size="icon-xs" aria-label="Stage settings" onClick={handleClick}>
      <Icon name="ellipsis" size="sm" color="muted" />
    </Button>
  );
}
